/**
 * **舞台の設定とその時刻での値**（Issue #643。仕様は `docs/duel-timing.md`）。純関数。
 *
 * タイミング調節のページでは、舞台の設定（フィールドオブジェクト・カメラ・HUD・層の濃さ）を**タイムラインに置ける**。
 * 置いた「設定の変化」は**再生位置がそこを通ったら**その値に変わる（次の変化まで続く）。
 *
 *   その時刻の設定 ＝ 既定（`SETTING_DEFAULTS`）⊕ プロジェクトの base ⊕ 時刻が ms 以下の変化（時刻の順・同時刻は並びの順）
 *
 * 1 つの変化は**一部の項目だけ**を書く（書いていない項目は前の値のまま）。層の濃さは層ごとに上書きする。
 * **名前の一覧はサーバーの `ocg/fx_docs.py` と 1 対 1**（`timingProject.test.js` が突き合わせる）。
 */
import { FIELD_LAYERS, OBJECT_KINDS, OBJECT_PRESETS, OPACITY_PRESETS } from '../duelEditor/core/theme';
import { DEFAULT_SHOT_NAME, SHOTS, SHOT_LABELS } from '../duelEditor/stage/camera';

export const SETTING_FIELDS = Object.freeze(['live', 'virtual', 'empty']);
export const SETTING_FIELD_LABELS = Object.freeze({ live: '実写', virtual: '仮想', empty: '空' });
export const SETTING_SHOTS = Object.freeze(Object.keys(SHOTS));
export const SETTING_SHOT_LABELS = SHOT_LABELS;
export const SETTING_FIELD_LAYERS = Object.freeze(FIELD_LAYERS.map((l) => l.name));
export const SETTING_OBJECT_LAYERS = Object.freeze(OBJECT_KINDS.map((k) => k.name));
export const SETTING_KEYS = Object.freeze(['field', 'shot', 'hud', 'zoneLabels', 'fieldLayers', 'objectLayers', 'grade']);
// 色の調整（Issue #915。Godot が描く。Web は落とさず通すだけ）
export const GRADE_TARGETS = Object.freeze(['background', 'field', 'hud', 'whole']);
export const GRADE_KEYS = Object.freeze({ brightness: [0, 3], contrast: [0, 3], saturate: [0, 3], hue: [-180, 180] });

/** 何も書かれていないときの値（今までの `/duel-editor` の既定と同じ） */
export const SETTING_DEFAULTS = Object.freeze({
  field: 'live',
  shot: DEFAULT_SHOT_NAME,
  hud: true,
  zoneLabels: true,
  fieldLayers: Object.freeze({ ...OPACITY_PRESETS.live }),
  objectLayers: Object.freeze({ ...OBJECT_PRESETS.live }),
});

/** 画面に出す名前 */
export const SETTING_LABELS = Object.freeze({
  field: 'フィールド', shot: 'カメラ', hud: 'HUD', zoneLabels: 'ゾーンの文字', fieldLayers: '盤の層', objectLayers: 'オブジェクト', grade: '色の調整',
});
export const LAYER_LABELS = Object.freeze({
  ...Object.fromEntries(FIELD_LAYERS.map((l) => [l.name, l.label])),
  ...Object.fromEntries(OBJECT_KINDS.map((k) => [k.name, k.label])),
});

const isFin = Number.isFinite;
const clamp01 = (v) => Math.min(1, Math.max(0, v));

/**
 * **0 か 1 だけの層**（Issue #644。ユーザー判断 2026-10-01）。カードは濃さではなく**出す / 出さない**で、
 * 出さない区間では motion（カードを動かす演出）を作らない＝関数を呼ばない（`duelEffects.js::duelTypeSpec`）
 */
export const BINARY_LAYERS = Object.freeze(['card']);

/** 濃さの表を整える（知らない名前・数でないものは落とす。0/1 だけの層は 0.5 で丸める） */
function cleanOpacities(value, names) {
  if (!value || typeof value !== 'object') return null;
  const out = {};
  names.forEach((n) => {
    if (!isFin(value[n])) return;
    out[n] = BINARY_LAYERS.includes(n) ? (value[n] >= 0.5 ? 1 : 0) : clamp01(value[n]);
  });
  return Object.keys(out).length ? out : null;
}

/**
 * 設定（一部）を仕様どおりに整える。**知らない項目・範囲外の値は落とす**（保存するものは必ず仕様どおりになる）
 * @returns 整えた設定（空なら `{}`）
 */
export function cleanSettings(value) {
  const v = value && typeof value === 'object' ? value : {};
  const out = {};
  if (SETTING_FIELDS.includes(v.field)) out.field = v.field;
  if (SETTING_SHOTS.includes(v.shot)) out.shot = v.shot;
  if (typeof v.hud === 'boolean') out.hud = v.hud;
  if (typeof v.zoneLabels === 'boolean') out.zoneLabels = v.zoneLabels;
  const fl = cleanOpacities(v.fieldLayers, SETTING_FIELD_LAYERS);
  if (fl) out.fieldLayers = fl;
  const ol = cleanOpacities(v.objectLayers, SETTING_OBJECT_LAYERS);
  if (ol) out.objectLayers = ol;
  const g = cleanGrade(v.grade);
  if (g) out.grade = g;
  return out;
}

/** 色の調整を整える（対象ごと・鍵ごと。知らない対象・鍵・範囲外は落とす。空なら null） */
export function cleanGrade(value) {
  if (!value || typeof value !== 'object') return null;
  const out = {};
  for (const target of GRADE_TARGETS) {
    const raw = value[target];
    if (!raw || typeof raw !== 'object') continue;
    const g = {};
    for (const [key, [lo, hi]] of Object.entries(GRADE_KEYS)) {
      const n = Number(raw[key]);
      if (raw[key] !== undefined && raw[key] !== null && Number.isFinite(n) && n >= lo && n <= hi) g[key] = n;
    }
    if (Object.keys(g).length) out[target] = g;
  }
  return Object.keys(out).length ? out : null;
}

/** 設定の重ね（`over` に書いてある項目だけ上書き。層の濃さは層ごと） */
export function mergeSettings(under, over) {
  const o = cleanSettings(over);
  const out = { ...under, ...o };
  if (o.fieldLayers) out.fieldLayers = { ...(under?.fieldLayers ?? {}), ...o.fieldLayers };
  if (o.objectLayers) out.objectLayers = { ...(under?.objectLayers ?? {}), ...o.objectLayers };
  if (o.grade) {
    out.grade = { ...(under?.grade ?? {}) };
    for (const [target, g] of Object.entries(o.grade)) out.grade[target] = { ...(out.grade[target] ?? {}), ...g };
  }
  return out;
}

/** プロジェクトの設定の入れ物を整える（`{ base, changes }`。変化は時刻の順・id は重ならない・空の変化は落とす） */
export function cleanTimingSettings(value) {
  const base = cleanSettings(value?.base);
  const seen = new Set();
  const changes = (Array.isArray(value?.changes) ? value.changes : [])
    .map((c) => ({ id: String(c?.id ?? ''), ms: Math.max(0, Math.round(Number(c?.ms) || 0)), set: cleanSettings(c?.set) }))
    .filter((c) => /^[A-Za-z0-9_-]{1,40}$/.test(c.id) && !seen.has(c.id) && seen.add(c.id) && Object.keys(c.set).length)
    .map((c, i) => ({ ...c, order: i }))
    .sort((a, b) => a.ms - b.ms || a.order - b.order)
    .map(({ order, ...c }) => c);
  return { base, changes };
}

/**
 * **その時刻の設定**（完全な形。既定 ⊕ base ⊕ 時刻が ms 以下の変化）
 * @param settings プロジェクトの `{ base, changes }`
 */
export function settingsAt(settings, ms) {
  const { base, changes } = cleanTimingSettings(settings);
  let out = mergeSettings(SETTING_DEFAULTS, base);
  for (const c of changes) {
    if (c.ms > ms) break;
    out = mergeSettings(out, c.set);
  }
  return out;
}


/** 空いている id（`c1`, `c2`, …） */
export function nextChangeId(changes = []) {
  const used = new Set(changes.map((c) => c.id));
  for (let i = 1; ; i += 1) if (!used.has(`c${i}`)) return `c${i}`;
}

/** 変化の一言（タイムラインの印の名前） */
export function describeChange(set = {}) {
  const parts = [];
  if (set.field) parts.push(SETTING_FIELD_LABELS[set.field]);
  if (set.shot) parts.push(`カメラ ${SETTING_SHOT_LABELS[set.shot] ?? set.shot}`);
  if (typeof set.hud === 'boolean') parts.push(`HUD ${set.hud ? '出す' : '消す'}`);
  if (typeof set.zoneLabels === 'boolean') parts.push(`文字 ${set.zoneLabels ? '出す' : '消す'}`);
  if (set.fieldLayers) parts.push('盤の層');
  if (set.objectLayers) parts.push('オブジェクト');
  return parts.join('・') || '（変化なし）';
}
